import {
  clearFavoriteChannels,
  clearLastWatchedEpgs,
  clearLiveCategories,
  clearLiveChannels,
  clearMessages,
  clearMostWatchedEpgs,
  clearProfileSettings,
  getAppAudiolangs,
  getAppResolutions,
  getAppSublangs,
  getChannelSettings,
  getLastWatchedEpgs,
  getListsForManyCategories,
  getLiveCategories,
  getLiveChannels,
  getLogos,
  getMessages,
  getMostWatchedEpgs,
  getProfileSettings,
  getRecordedCategories,
  getServerTime,
  getUserDetails,
  refreshToken as refreshTokenAction,
  removeUserDetails,
  resetRecorded,
  retrieveBanners,
  retrievePrismicRadioImages,
  retrievePrismicScreensaverImages
} from 'common/actions';
import variables from 'common/config/variables';
import { AppDispatch, StoreState } from 'index';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import useMessages from './useMessages';

/**
 * Custom hook that handles fetching and clearing required data.
 * @returns {Object} An object containing the following functions:
 *   - get: A function that retrieves the required data by calling the `fetchData` function after refreshing the token.
 *   - clear: A function that clears the required data.
 *   - refreshToken: A function that refreshes the user token.
 *   - hasRequiredData: A function that checks if the required data is available.
 */
const useRequiredData = () => {
  const dispatch = useDispatch<AppDispatch>();
  const storeData = useSelector(
    (store: StoreState) => ({
      liveChannels: store.liveChannels,
      liveCategories: store.liveCategories,
      i18n: store.i18n,
      recorded: store.epgs.recorded,
      vod: store.epgs.vod,
      mostWatched: store.mostWatched,
      lastWatched: store.lastWatched,
      prismic: store.prismic,
      profileSettings: store.profileSettings,
      userDetails: store.userDetails,
      serverTime: store.serverTime,
      shouldWaitForPlayer: variables.LOADING_WAITS_FOR_PLAYER ? !store.videoPlayer.isPlaying : false
    }),
    shallowEqual
  );

  // Start and stop the timer when the required data
  // is fetched and cleared
  const { stopTimer, startTimer } = useMessages();

  const clear = useCallback(() => {
    dispatch(clearLastWatchedEpgs());
    dispatch(clearMostWatchedEpgs());
    dispatch(clearLiveCategories());
    dispatch(clearLiveChannels());
    dispatch(resetRecorded());
    dispatch(removeUserDetails());
    dispatch(clearFavoriteChannels());
    dispatch(clearMessages());
    dispatch(clearProfileSettings());
    stopTimer();
  }, []);

  const fetchData = useCallback(async () => {
    await dispatch(getServerTime());
    await dispatch(getProfileSettings());
    await dispatch(getChannelSettings());
    await dispatch(getRecordedCategories(true));
    await Promise.all([
      dispatch(getLiveChannels()),
      dispatch(getLiveCategories()),
      dispatch(getLogos()),
      dispatch(getRecordedCategories()),
      dispatch(getListsForManyCategories(true)),
      dispatch(getUserDetails()),
      dispatch(retrievePrismicRadioImages()),
      dispatch(retrievePrismicScreensaverImages()),
      dispatch(retrieveBanners()),
      dispatch(getMostWatchedEpgs()),
      dispatch(getLastWatchedEpgs()),
      dispatch(getAppAudiolangs()),
      dispatch(getAppSublangs()),
      dispatch(getAppResolutions()),
      dispatch(getMessages())
    ]);
    startTimer();
  }, []);

  const loadingRequiredData = useMemo(() => {
    return (
      isEmpty(storeData.i18n) ||
      isEmpty(storeData.liveCategories) ||
      isEmpty(storeData.liveChannels) ||
      isNil(storeData.recorded.categories) ||
      isNil(storeData.vod.categories) ||
      isEmpty(storeData.userDetails) ||
      isEmpty(storeData.serverTime) ||
      isNil(storeData.mostWatched) ||
      isNil(storeData.lastWatched)
    );
  }, [storeData]);

  /**
   * @param {Function} onRefresh - Optional function to call after refreshing the token
   * @returns {Promise<Boolean>} true if everything OK, false on error
   */
  const refreshToken = useCallback(async (onRefresh?: (() => Promise<void>) | undefined) => {
    //
    return (await dispatch(refreshTokenAction(onRefresh))) as unknown as boolean;
  }, []);

  /**
   * Retrieves the required data by calling the `fetchData` function after refreshing the token.
   * @returns {Promise<boolean>} A promise that resolves to the fetched data.
   */
  const get = useCallback(async () => {
    try {
      return await refreshToken(fetchData);
    } catch (error) {
      console.error('Error while fetching required data', error);
      return false;
    }
  }, [refreshToken]);

  return { get, clear, refreshToken, loadingRequiredData };
};
export default useRequiredData;
